import React, { useContext } from 'react';
import {
  OAuthFlow,
  AutoLoginFlow,
  FinixFlow,
  FormFlow,
  CollectACHDetailsFlow,
  CollectACHDetailsWithStreetAddressFlow,
} from './flows/index';
import {
  OAuthView,
  FinixView,
  JsonFormView,
  LoadingView,
  TwoFactorSimplifiedView,
  CollectACHDetailsView,
  CollectACHDetailsWithStreetAddressView,
  CollectAddressViewEmbeddable,
} from './views/index';
import { actions, StateContext } from '../../state/index';
import {
  HTTP_REQUEST_TYPES,
  FLOW_TYPES,
  TOKEN_TYPES,
  LOGGING,
} from '../../utils/enums';
import { postRecipientLogin } from '../../api/index';
import { HttpRequestException } from '../../utils/errors';
import { logger, logHttpError } from '../../utils/logging';
import { find } from 'lodash/collection';
import { get } from 'lodash/object';

const RecipientLoginFlows = () => {
  const [applicationState, dispatch] = useContext(StateContext);
  const { configuration, tokens, selectedPaymentNetwork, payment, accounts } =
    applicationState;

  // TODO change skipDefaults to pre and post effects since all flows will require setting tokens upon successful http requests
  const recipientLogin = async ({
    dataForRecipientLogin,
    skipDefaultPostEffects = false,
    additionalLogDataForDebugging,
    customLogData, // customize log data in case there is sensitive info in /recipienLogin data (e.g. bank account info)
  }) => {
    const dataForLogs = customLogData || dataForRecipientLogin;

    try {
      logger({
        event: LOGGING.EVENTS.RECIPIENT_LOGIN_ATTEMPTED,
        paymentNetwork: selectedPaymentNetwork,
        paymentId: payment.id,
        data: dataForLogs,
      });

      const response = await postRecipientLogin(
        payment.id,
        tokens[TOKEN_TYPES.VALIDATE_PAYMENT].access_token,
        selectedPaymentNetwork,
        {
          ...dataForRecipientLogin,
          account_id: get(
            find(
              accounts,
              (account) => account.paymentNetwork === selectedPaymentNetwork
            ),
            'id',
            ''
          ),
        }
      );

      logger({
        event: LOGGING.EVENTS.RECIPIENT_LOGIN_SUCCEEDED,
        paymentNetwork: selectedPaymentNetwork,
        paymentId: payment.id,
      });

      if (skipDefaultPostEffects) {
        return response;
      } else {
        dispatch(
          actions.tokens.setToken(TOKEN_TYPES.RECIPIENT_LOGIN, response.data)
        );
        return response;
      }
    } catch (error) {
      if (skipDefaultPostEffects) {
        throw error;
      } else {
        const httpRequestException = HttpRequestException(
          HTTP_REQUEST_TYPES.RECIPIENT_LOGIN,
          error
        );

        logHttpError(httpRequestException, applicationState, {
          event: LOGGING.EVENTS.RECIPIENT_LOGIN_FAILED,
          paymentId: payment.id,
          data: dataForLogs,
          additionalLogDataForDebugging,
        });

        dispatch(
          actions.modals.showHttpErrorModal(configuration, {
            errorType: httpRequestException.errorType,
            serverResponse: httpRequestException.serverResponse,
            propsByHttpStatus: {
              default: {
                showChooseAnotherNetwork: false,
                actions: [
                  {
                    label: 'Start Over',
                    onClick: () => dispatch(actions.startOver()),
                  },
                ],
              },
              400: {
                showServerErrorMessage: true,
                showFaqLink: false,
                actions: [
                  {
                    label: 'Start Over',
                    onClick: () => dispatch(actions.startOver()),
                  },
                ],
              },
            },
          })
        );
      }
    }
  };

  let Flow, View;
  switch (configuration.recipientLogin.flow) {
    case FLOW_TYPES.RECIPIENT_LOGIN.OAUTH:
      Flow = OAuthFlow;
      View = OAuthView;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.AUTO_LOGIN:
      Flow = AutoLoginFlow;
      View = <LoadingView />;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.FINIX:
      Flow = FinixFlow;
      View = FinixView;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.FORM:
      Flow = FormFlow;
      View = JsonFormView;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.TWO_FACTOR:
      Flow = FormFlow;
      View = TwoFactorSimplifiedView;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.COLLECT_ADDRESS:
      Flow = FormFlow;
      View = CollectAddressViewEmbeddable;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.COLLECT_ACH_DETAILS:
      Flow = CollectACHDetailsFlow;
      View = CollectACHDetailsView;
      break;
    case FLOW_TYPES.RECIPIENT_LOGIN.COLLECT_ACH_DETAILS_STREET_ADDRESS:
      Flow = CollectACHDetailsWithStreetAddressFlow;
      View = CollectACHDetailsWithStreetAddressView;
      break;
    default:
      Flow = () => <div>{configuration.recipientLogin.flow}</div>;
      View = (
        <>
          <div>{configuration.recipientLogin.flow}</div>
          <div>{selectedPaymentNetwork}</div>
        </>
      );
      break;
  }

  return <Flow recipientLogin={recipientLogin}>{View}</Flow>;
};

export default RecipientLoginFlows;
